// Blocs composables d'une signature : ordre et visibilité (brands.blocks, migration 20260917).
//
// Le rendu parcourt la liste dans l'ordre et saute les blocs invisibles ; un bloc dont la donnée
// est vide ne produit rien. `blocks` null ou invalide = ordre par défaut ci-dessous, jamais d'erreur.
// Un bloc absent d'une configuration enregistrée (bloc ajouté depuis) est rangé à la fin, visible.
import type { BlockConfig, BlockId, Brand, Person } from './types.ts';
import { socialLinks } from './social.ts';
import { safeUrl } from './util.ts';

export const BLOCK_IDS: readonly BlockId[] = [
  'name',
  'title',
  'venue',
  'phone_mobile',
  'phone_office',
  'email',
  'address',
  'website',
  'social',
  'legal',
  'banner',
];

/** Ordre du rendu d'avant la migration : identité, contacts, site, réseaux, bannière, mention. */
export const DEFAULT_BLOCK_ORDER: readonly BlockId[] = [
  'name',
  'title',
  'venue',
  'phone_mobile',
  'phone_office',
  'email',
  'address',
  'website',
  'social',
  'banner',
  'legal',
];

export const BLOCK_LABELS: Record<BlockId, string> = {
  name: 'Nom',
  title: 'Fonction',
  venue: 'Nom du lieu',
  phone_mobile: 'Téléphone mobile',
  phone_office: 'Téléphone fixe',
  email: 'Adresse e-mail',
  address: 'Adresse postale',
  website: 'Site web',
  social: 'Réseaux sociaux',
  legal: 'Mention légale',
  banner: 'Bannière',
};

export function defaultBlocks(): BlockConfig[] {
  return DEFAULT_BLOCK_ORDER.map((id) => ({ id, visible: true }));
}

const isBlockId = (v: unknown): v is BlockId => typeof v === 'string' && (BLOCK_IDS as readonly string[]).includes(v);

/** Liste exploitable quoi qu'il y ait en base : entrées inconnues ou en double ignorées, blocs manquants ajoutés à la fin. */
export function normalizeBlocks(input: unknown): BlockConfig[] {
  if (!Array.isArray(input)) return defaultBlocks();
  const out: BlockConfig[] = [];
  const seen = new Set<BlockId>();
  for (const item of input) {
    if (!item || typeof item !== 'object') continue;
    const { id, visible } = item as { id?: unknown; visible?: unknown };
    if (!isBlockId(id) || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, visible: visible !== false });
  }
  if (out.length === 0) return defaultBlocks();
  for (const id of DEFAULT_BLOCK_ORDER) if (!seen.has(id)) out.push({ id, visible: true });
  return out;
}

/** Blocs de la colonne de texte (à côté du logo) et blocs pleine largeur (bandes du haut ou du bas). */
export const TEXT_BLOCKS: readonly BlockId[] = ['name', 'title', 'venue', 'phone_mobile', 'phone_office', 'email', 'address', 'website', 'social'];
export const FULL_WIDTH_BLOCKS: readonly BlockId[] = ['banner', 'legal'];

export interface BlockLayout {
  above: BlockConfig[]; // pleine largeur, avant le premier bloc texte
  text: BlockConfig[]; // colonne de texte, dans l'ordre
  below: BlockConfig[]; // pleine largeur, après
}

/** Découpe la liste en trois zones, exactement comme le rendu la lit (render.ts, fullWidthRows). */
export function layoutFromBlocks(blocks: unknown): BlockLayout {
  const list = normalizeBlocks(blocks);
  const firstText = list.findIndex((b) => TEXT_BLOCKS.includes(b.id));
  const above: BlockConfig[] = [];
  const text: BlockConfig[] = [];
  const below: BlockConfig[] = [];
  list.forEach((b, i) => {
    if (TEXT_BLOCKS.includes(b.id)) text.push(b);
    else if (firstText >= 0 && i < firstText) above.push(b);
    else below.push(b);
  });
  return { above, text, below };
}

export function layoutToBlocks(layout: BlockLayout): BlockConfig[] {
  return normalizeBlocks([...layout.above, ...layout.text, ...layout.below]);
}

/**
 * Ce qu'un bloc afficherait pour cette personne et cette adresse d'envoi (texte brut, pour la
 * maquette de l'écran Chartes). Chaîne vide = le bloc ne produirait rien.
 */
export function blockSummary(brand: Brand, person: Person, sendAs: string, id: BlockId): string {
  const shared = person.kind === 'shared';
  switch (id) {
    case 'name':
      return person.display_name || [person.first_name, person.last_name].filter(Boolean).join(' ') || (shared ? brand.name : person.primary_email);
    case 'title':
      return shared ? person.job_title || 'Nous contacter' : person.job_title || '';
    case 'venue':
      return shared ? '' : brand.name;
    case 'phone_mobile':
      return person.phone_mobile || '';
    case 'phone_office':
      return person.phone_office || (shared ? brand.phone || '' : '');
    case 'email':
      return sendAs;
    case 'address':
      return brand.address || '';
    case 'website': {
      const site = safeUrl(brand.website_url);
      return site ? site.replace(/^https?:\/\//i, '').replace(/\/$/, '') : '';
    }
    case 'social':
      return socialLinks(brand).map((l) => l.network.label).join(' · ');
    case 'banner':
      return 'Campagne retenue pour cette personne (une bannière permanente est une campagne sans dates)';
    case 'legal':
      return brand.legal_mention || '';
  }
}

/** Déplace un bloc dans la liste (glisser-déposer ou flèches de l'écran Chartes). */
export function moveBlock(blocks: BlockConfig[], from: number, to: number): BlockConfig[] {
  const list = blocks.map((b) => ({ ...b }));
  if (from < 0 || from >= list.length || to < 0 || to >= list.length || from === to) return list;
  const [moved] = list.splice(from, 1);
  list.splice(to, 0, moved);
  return list;
}
